"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCWIcon } from "@/components/ui/refresh-cw";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Cell, Pie, PieChart } from "recharts";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2Icon, ClockIcon, XCircleIcon, LoaderIcon, FileTextIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// ─── Types ─────────────────────────────────────────────────────────────────────

type ParseJobStatusCounts = {
  PARSE_JOB: number;
  PROCESSING: number;
  COMPLETED: number;
  FAILED: number;
  PENDING: number;
  total: number;
};

type ActiveParseJob = {
  id: string;
  name: string;
  external_parse_job_id: string | null;
  created_at: string;
  processing_status: string;
};

type PollerRun = {
  runid: number;
  status: string;
  return_message: string | null;
  start_time: string;
  end_time: string | null;
};

type ParseJobHealthResponse = {
  counts: ParseJobStatusCounts;
  activeJobs: ActiveParseJob[];
  pollerSchedule: string;
  pollerLastRun: PollerRun | null;
  pollerRecentRuns: PollerRun[];
};

type Props = { onBack: () => void };

// ─── Config ────────────────────────────────────────────────────────────────────

const chartConfig = {
  "parse-job":  { label: "Queued",     color: "hsl(var(--chart-1))" },
  "processing": { label: "Processing", color: "hsl(var(--chart-2))" },
  "completed":  { label: "Completed",  color: "hsl(var(--chart-3))" },
  "failed":     { label: "Failed",     color: "hsl(var(--chart-4))" },
  "pending":    { label: "Pending",    color: "hsl(var(--chart-5))" },
} satisfies ChartConfig;

const PIE_KEY_MAP: Record<string, keyof typeof chartConfig> = {
  PARSE_JOB:  "parse-job",
  PROCESSING: "processing",
  COMPLETED:  "completed",
  FAILED:     "failed",
  PENDING:    "pending",
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatAge(iso: string) {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatDuration(start: string, end: string | null) {
  if (!end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function truncate(str: string, max = 48) {
  return str.length > max ? str.slice(0, max) + "…" : str;
}

// ─── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon: Icon,
  loading,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <p className="text-3xl font-bold">{value}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Panel ─────────────────────────────────────────────────────────────────────

export function ParseJobsPanel({ onBack }: Props) {
  const [data, setData] = useState<ParseJobHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not authenticated");

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-parse-job-health`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: ParseJobHealthResponse = await res.json();
      setData(json);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelJob = useCallback(async (fileId: string) => {
    setCancellingIds((prev) => new Set(prev).add(fileId));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not authenticated");

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/cancel-parse-job`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ fileId }),
        }
      );
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel job");
    } finally {
      setCancellingIds((prev) => { const s = new Set(prev); s.delete(fileId); return s; });
    }
  }, [load]);

  const counts = data?.counts;
  const activeJobs = data?.activeJobs ?? [];
  const pollerRecentRuns = data?.pollerRecentRuns ?? [];
  const pollerLastRun = data?.pollerLastRun ?? null;

  const pieData = counts
    ? Object.entries(PIE_KEY_MAP)
        .map(([status, key]) => ({
          name: chartConfig[key].label,
          value: counts[status as keyof typeof counts] as number,
          color: chartConfig[key].color,
        }))
        .filter((d) => d.value > 0)
    : [];

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        variant="collection"
        title="Large File Monitoring"
        onBack={onBack}
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCWIcon size={14} />
            Refresh
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-6">

          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : null}

          {/* Poller status */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Parse Poller</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="relative flex size-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex size-2.5 rounded-full bg-green-500" />
                  </span>
                  <span className="text-sm font-medium">Active</span>
                  <span className="text-sm text-muted-foreground">— {data?.pollerSchedule ?? "Every minute"}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {pollerLastRun ? (
                    <>
                      <span>Last run {formatAge(pollerLastRun.start_time)}</span>
                      <Badge
                        variant="secondary"
                        className={cn("text-xs", pollerLastRun.status === "succeeded"
                          ? "text-green-700 dark:text-green-400"
                          : "text-destructive"
                        )}
                      >
                        {pollerLastRun.status}
                      </Badge>
                    </>
                  ) : loading ? (
                    <Skeleton className="h-4 w-28" />
                  ) : (
                    <span>No runs recorded</span>
                  )}
                  {lastRefreshed ? (
                    <span className="border-l border-border pl-3">Checked {formatAge(lastRefreshed.toISOString())}</span>
                  ) : null}
                </div>
              </div>

              {/* Recent runs table */}
              {pollerRecentRuns.length > 0 ? (
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border text-left text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Run ID</th>
                        <th className="px-3 py-2 font-medium">Started</th>
                        <th className="px-3 py-2 font-medium">Duration</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pollerRecentRuns.map((run) => (
                        <tr key={run.runid} className="border-b border-border last:border-0 hover:bg-muted/40">
                          <td className="px-3 py-2 font-mono text-muted-foreground">{run.runid}</td>
                          <td className="px-3 py-2 text-muted-foreground">{formatAge(run.start_time)}</td>
                          <td className="px-3 py-2 tabular-nums">{formatDuration(run.start_time, run.end_time)}</td>
                          <td className="px-3 py-2">
                            <Badge
                              variant="secondary"
                              className={cn("text-xs", run.status === "succeeded"
                                ? "text-green-700 dark:text-green-400"
                                : "text-destructive"
                              )}
                            >
                              {run.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground max-w-xs truncate">
                            {run.return_message ? truncate(run.return_message, 80) : <span className="italic">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : !loading ? (
                <p className="text-xs text-muted-foreground">No run history available.</p>
              ) : null}
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard label="Active Jobs"  value={counts?.PARSE_JOB  ?? 0} icon={ClockIcon}        loading={loading} />
            <StatCard label="Processing"   value={counts?.PROCESSING  ?? 0} icon={LoaderIcon}       loading={loading} />
            <StatCard label="Completed"    value={counts?.COMPLETED   ?? 0} icon={CheckCircle2Icon} loading={loading} />
            <StatCard label="Failed"       value={counts?.FAILED      ?? 0} icon={XCircleIcon}      loading={loading} />
            <StatCard label="Total"        value={counts?.total       ?? 0} icon={FileTextIcon}      loading={loading} />
          </div>

          {/* Pie chart */}
          {!loading && pieData.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Status Distribution</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                <ChartContainer config={chartConfig} className="h-48 w-48 shrink-0">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={72}
                      paddingAngle={2}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                  </PieChart>
                </ChartContainer>
                <div className="flex flex-col gap-2 pt-2">
                  {pieData.map((entry) => (
                    <div key={entry.name} className="flex items-center gap-2 text-sm">
                      <span
                        className="inline-block size-3 shrink-0 rounded-full"
                        style={{ background: entry.color }}
                      />
                      <span className="text-muted-foreground">{entry.name}</span>
                      <span className="ml-auto font-medium tabular-nums">{entry.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : !loading && pieData.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No large documents processed yet.
              </CardContent>
            </Card>
          ) : null}

          {/* Active jobs table */}
          {activeJobs.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">
                  Active Parse Jobs ({activeJobs.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="px-4 py-2 font-medium">File</th>
                        <th className="px-4 py-2 font-medium">Job ID</th>
                        <th className="px-4 py-2 font-medium">Submitted</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                        <th className="px-4 py-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeJobs.map((job) => {
                        const isCancelling = cancellingIds.has(job.id);
                        return (
                        <tr key={job.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                          <td className="px-4 py-3 font-medium">{truncate(job.name)}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            {job.external_parse_job_id
                              ? truncate(job.external_parse_job_id, 24)
                              : <span className="italic">—</span>}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{formatAge(job.created_at)}</td>
                          <td className="px-4 py-3">
                            <Badge variant="secondary" className="gap-1 text-xs">
                              <LoaderIcon className="size-3 animate-spin" />
                              Queued
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                                  disabled={isCancelling}
                                >
                                  {isCancelling
                                    ? <LoaderIcon className="size-3 animate-spin" />
                                    : "Cancel"}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Cancel parse job?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will mark <span className="font-medium text-foreground">{job.name}</span> as failed. The file will need to be re-uploaded to process again.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Keep</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() => cancelJob(job.id)}
                                  >
                                    Cancel job
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : !loading ? (
            <Card>
              <CardContent className="py-6 text-center text-sm text-muted-foreground">
                No active parse jobs.
              </CardContent>
            </Card>
          ) : null}

        </div>
      </div>
    </div>
  );
}
